import React, { useMemo } from 'react';
import {
  Box,
  ButtonBase,
  Checkbox,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { buildControlMenuProps } from './ControlMenu';
import { getControlTokens } from './controlTokens';

const makeOptionKey = (groupId, value) => `${groupId}::${value}`;

const splitOptionKey = (key) => {
  const separatorIndex = String(key).indexOf('::');
  if (separatorIndex === -1) return { groupId: '', value: key };
  return {
    groupId: key.slice(0, separatorIndex),
    value: key.slice(separatorIndex + 2),
  };
};

const normalizeGroupValues = (groups, value) => groups.reduce((acc, group) => {
  const allowed = new Set((group.options || []).map((option) => option.value));
  acc[group.id] = Array.isArray(value?.[group.id])
    ? value[group.id].filter((item) => allowed.has(item))
    : [];
  return acc;
}, {});

function ControlGroupedMultiSelect({
  context = 'filterBar',
  menuContext = context,
  groups = [],
  value = {},
  onChange,
  width = 210,
  placeholder = 'Filters',
  allSelectedLabel = 'All filters',
  selectedLabel = 'filters',
  ariaLabel = placeholder,
  sx,
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const normalizedValue = useMemo(() => normalizeGroupValues(groups, value), [groups, value]);
  const selectedKeys = useMemo(() => (
    groups.flatMap((group) => (normalizedValue[group.id] || []).map((optionValue) => makeOptionKey(group.id, optionValue)))
  ), [groups, normalizedValue]);
  const totalOptionCount = groups.reduce((sum, group) => sum + (group.options || []).length, 0);
  const selectedCount = selectedKeys.length;
  const displayLabel = selectedCount === 0
    ? placeholder
    : selectedCount === totalOptionCount
      ? allSelectedLabel
      : `${selectedCount} ${selectedLabel}`;
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  const emitChange = (nextValue) => {
    if (onChange) onChange(nextValue);
  };

  const handleChange = (event) => {
    const keys = Array.isArray(event.target.value) ? event.target.value : [];
    const next = groups.reduce((acc, group) => {
      acc[group.id] = [];
      return acc;
    }, {});

    keys.forEach((key) => {
      const { groupId, value: optionValue } = splitOptionKey(key);
      if (next[groupId]) next[groupId].push(optionValue);
    });

    emitChange(next);
  };

  const setGroupValues = (groupId, groupValues) => {
    emitChange({
      ...normalizedValue,
      [groupId]: groupValues,
    });
  };

  return (
    <Box
      data-control-context={context}
      data-control-kind="grouped-multi-select"
      sx={{
        width: resolvedWidth,
        minWidth: resolvedWidth,
        maxWidth: '100%',
        height: tokens.height,
        minHeight: tokens.height,
        display: 'flex',
        alignItems: 'stretch',
        ...sx,
      }}
    >
      <Select
        multiple
        value={selectedKeys}
        onChange={handleChange}
        displayEmpty
        size="small"
        inputProps={{ 'aria-label': ariaLabel }}
        renderValue={() => (
          <Typography
            component="span"
            sx={{
              color: tokens.textColor,
              fontSize: tokens.fontSize,
              fontWeight: 400,
              lineHeight: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayLabel}
          </Typography>
        )}
        MenuProps={buildControlMenuProps(theme, menuContext, {
          PaperProps: {
            elevation: 4,
            sx: {
              minWidth: 260,
              maxHeight: '68vh',
            },
          },
        })}
        sx={{
          width: '100%',
          height: tokens.height,
          backgroundColor: tokens.surface,
          fontSize: tokens.fontSize,
          '& .MuiSelect-select': {
            height: tokens.height,
            minHeight: `${tokens.height}px !important`,
            display: 'flex',
            alignItems: 'center',
            py: 0,
            pl: 1,
            pr: 3.25,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.borderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {groups.map((group) => {
          const groupOptions = group.options || [];
          const groupValues = normalizedValue[group.id] || [];
          const selectedInGroup = groupValues.length;

          return [
            <ListSubheader
              key={`${group.id}-header`}
              disableSticky
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                height: 30,
                px: 1.25,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.03)',
                borderTop: `1px solid ${tokens.dividerColor}`,
                lineHeight: 1,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: tokens.textColor,
                  fontSize: tokens.labelFontSize,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                {group.label} ({selectedInGroup}/{groupOptions.length})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ButtonBase
                  component="span"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setGroupValues(group.id, groupOptions.map((option) => option.value));
                  }}
                  sx={{
                    color: tokens.selectedColor,
                    fontSize: tokens.labelFontSize,
                    fontWeight: 800,
                    px: 0.25,
                  }}
                >
                  All
                </ButtonBase>
                <ButtonBase
                  component="span"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setGroupValues(group.id, []);
                  }}
                  sx={{
                    color: tokens.textColor,
                    fontSize: tokens.labelFontSize,
                    fontWeight: 800,
                    px: 0.25,
                  }}
                >
                  Clear
                </ButtonBase>
              </Box>
            </ListSubheader>,
            ...groupOptions.map((option) => {
              const optionKey = makeOptionKey(group.id, option.value);
              const checked = groupValues.includes(option.value);

              return (
                <MenuItem
                  key={optionKey}
                  value={optionKey}
                  disabled={option.disabled}
                  sx={{
                    minHeight: 26,
                    height: 26,
                    py: 0.25,
                    px: 1.25,
                    gap: 0.75,
                    backgroundColor: checked ? theme.palette.action.selected : 'transparent',
                    borderLeft: checked ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    sx={{
                      p: 0,
                      '& .MuiSvgIcon-root': { fontSize: tokens.iconSize },
                    }}
                  />
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: tokens.optionFontSize,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.label || option.value}
                  </Typography>
                </MenuItem>
              );
            }),
          ];
        })}
      </Select>
    </Box>
  );
}

export default ControlGroupedMultiSelect;
